import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ListChecks } from "@phosphor-icons/react";

/**
 * SubmissionsList — shared read-only widget listing form submissions.
 *
 * Used by:
 *   • Form Builder (admin) — passes `fetchUrl=/forms/{id}/submissions`
 *   • Portal "My submissions" (external user) — passes
 *     `fetchUrl=/portal/submissions` + `showForm=true` to add a
 *     "which form?" column.
 *
 * Props:
 *   apiClient   — the axios instance to use (admin JWT or portal token)
 *   fetchUrl    — endpoint that returns the submissions list
 *   showForm    — when true, render a "form" chip next to the submitter
 *   ticketLinkPrefix — where a submission row's ticket chip should link.
 *                      Admins go to /tickets/{id}; portal users go to
 *                      /portal/tickets/{id}. Pass a base path (no trailing slash).
 *   emptyLabel  — string shown when there are 0 submissions.
 *   testIdPrefix — unique test-id prefix so multiple lists on one page
 *                  don't collide (defaults to "submissions-list").
 */
export default function SubmissionsList({
  apiClient,
  fetchUrl,
  showForm = false,
  ticketLinkPrefix = "/tickets",
  emptyLabel = "No submissions yet.",
  testIdPrefix = "submissions-list",
}) {
  const [subs, setSubs] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setSubs(null);
    setError(false);
    apiClient.get(fetchUrl)
      .then((r) => { if (alive) setSubs(r.data || []); })
      .catch(() => { if (alive) { setSubs([]); setError(true); } });
    return () => { alive = false; };
  }, [apiClient, fetchUrl]);

  const toggle = (id) => {
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
  const renderVal = (v) => {
    if (v == null || v === "") return <span className="italic text-slate-400">—</span>;
    if (Array.isArray(v)) return v.length ? v.join(", ") : <span className="italic text-slate-400">—</span>;
    if (typeof v === "boolean") return v ? "Yes" : "No";
    return String(v);
  };

  return (
    <div className="hairline rounded-lg bg-white p-4 space-y-3" data-testid={testIdPrefix}>
      <div className="flex items-center gap-2">
        <ListChecks size={16} weight="duotone" className="text-[#4f4c90]" />
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Submissions</div>
        {subs !== null && (
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{subs.length}</span>
        )}
      </div>
      {subs === null && <div className="text-xs text-slate-500 italic">Loading…</div>}
      {error && subs !== null && subs.length === 0 && (
        <div className="text-xs text-red-600 italic">Couldn&apos;t load submissions.</div>
      )}
      {!error && subs !== null && subs.length === 0 && (
        <div className="text-xs text-slate-500 italic">{emptyLabel}</div>
      )}
      {subs !== null && subs.length > 0 && (
        <div className="divide-y divide-slate-100 -mx-4">
          {subs.map((s) => {
            const isOpen = expanded.has(s.id);
            return (
              <div key={s.id} className="px-4 py-2" data-testid={`${testIdPrefix}-row-${s.id}`}>
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-800 font-medium truncate flex items-center gap-2">
                      {s.submitted_by_name || s.submitted_by_email}
                      {showForm && s.form_title && (
                        <span className="chip !text-[#4f4c90] bg-[#4f4c90]/10 !border-[#4f4c90]/30 !text-[10px] !py-0.5">
                          {s.form_title}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono truncate">{s.submitted_by_email} · {fmt(s.submitted_at)}</div>
                  </div>
                  {s.ticket_number && !s.ticket_deleted ? (
                    <Link
                      to={`${ticketLinkPrefix}/${s.ticket_id}`}
                      className="chip !text-[#4f4c90] !border-[#4f4c90]/30 bg-[#4f4c90]/10 hover:bg-[#4f4c90]/20 !text-[11px] !py-0.5"
                      data-testid={`${testIdPrefix}-ticket-link-${s.id}`}
                      title={s.ticket_subject || "Open ticket"}
                    >
                      #{s.ticket_number}
                    </Link>
                  ) : s.ticket_number ? (
                    <span
                      className="chip !text-slate-500 !border-slate-300 bg-slate-100 !text-[11px] !py-0.5"
                      title="This ticket has been deleted"
                    >
                      #{s.ticket_number} (deleted)
                    </span>
                  ) : (
                    <span className="chip !text-slate-500 !border-slate-300 bg-slate-100 !text-[11px] !py-0.5">no ticket</span>
                  )}
                  <button
                    onClick={() => toggle(s.id)}
                    className="text-[11px] text-slate-500 hover:text-[#4f4c90] underline underline-offset-2"
                    data-testid={`${testIdPrefix}-toggle-${s.id}`}
                  >
                    {isOpen ? "Hide answers" : "View answers"}
                  </button>
                </div>
                {isOpen && (
                  <div className="mt-2 rounded-md bg-slate-50 border border-slate-200 p-2 space-y-1">
                    {(s.raw_answers || []).length === 0 && (
                      <div className="text-[11px] text-slate-500 italic">No custom answers on this submission.</div>
                    )}
                    {(s.raw_answers || []).map((a, i) => (
                      <div key={`${s.id}-${i}`} className="text-[12px] flex gap-2">
                        <span className="text-slate-500 font-mono min-w-[140px] shrink-0">{a.label}:</span>
                        <span className="text-slate-800 break-all">{renderVal(a.value)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
